import { useMemo } from 'react';
import { defineMessages, useIntl, type MessageValue } from 'react-intl';

import { IngredientCardV2 } from '@garnish/product';

import { type PortionOption } from '../../machine';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationPortionStepper = (props: Props) => {
  const { options, ingredientName, currentPortions, onSelectPortion, onRemove } = props;

  const { formatMessage, formatNumber } = useIntl();

  // ─── Derived ───────────────────────────────────────────────────────────

  const formattedOptions = useMemo(() => {
    return options.map((option) => {
      const hasExtraCost = option.extrasCost > 0;

      const price = formatNumber(option.extrasCost / 100, { style: 'currency', currency: 'USD' });
      const label = formatMessage(messages.portionLabel, { count: String(option.portions) });
      const priceLabel = hasExtraCost ? formatMessage(messages.priceLabel, { price }) : undefined;

      return { portions: option.portions, label, priceLabel };
    });
  }, [options, formatMessage, formatNumber]);

  // ─────────────────────────────────────────────────────────────────────

  const a11yLabel = formatMessage(messages.portionStepperLabel, { ingredient: ingredientName });
  const removeButtonA11yLabel = formatMessage(messages.removeLabel, { ingredient: ingredientName });

  // ─────────────────────────────────────────────────────────────────────

  return (
    <IngredientCardV2.PortionStepper
      options={formattedOptions}
      currentPortions={currentPortions}
      accessibilityLabel={a11yLabel}
      removeButtonAccessibilityLabel={removeButtonA11yLabel}
      onSelectPortion={onSelectPortion}
      onRemove={onRemove}
    />
  );
};

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly portionLabel: { readonly count: string };
  readonly priceLabel: { readonly price: MessageValue };
  readonly portionStepperLabel: { readonly ingredient: MessageValue };
  readonly removeLabel: { readonly ingredient: MessageValue };
}>({
  portionLabel: {
    defaultMessage: `{count, select,
      1 {Single}
      2 {Double}
      3 {Triple}
      other {{count}x}
    }`,
    description: 'Customization | Portion stepper | Portion label',
  },
  priceLabel: {
    defaultMessage: '+{price}',
    description: 'Customization | Portion stepper | Extra cost label',
  },
  portionStepperLabel: {
    defaultMessage: 'Select portion size for {ingredient}',
    description: 'Customization | Portion stepper | a11y label',
  },
  removeLabel: {
    defaultMessage: 'Remove {ingredient}',
    description: 'Customization | Portion stepper | Remove button a11y label',
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type Props = {
  options: PortionOption[];
  ingredientName: string;
  currentPortions: number;
  onSelectPortion: (portions: number) => void;
  onRemove: () => void;
};
